export type ServiceFaq = { q: string; a: string };

export type Service = {
  slug: string;
  name: string;
  navName: string;
  title: string;
  description: string;
  h1: string;
  tagline: string;
  problem: string;
  approach: string;
  deliverables: string[];
  techStack: string[];
  faq: ServiceFaq[];
  relatedSlugs: [string, string];
  relatedProductSlug?: 'ats-resume-helper' | 'acadly-ai' | 'viralcut';
  primaryKeyword: string;
};

export const services: Service[] = [
  {
    slug: 'custom-saas-development',
    name: 'Custom SaaS Development',
    navName: 'Custom SaaS',
    primaryKeyword: 'custom SaaS development company',
    title: 'Custom SaaS Development Company',
    description:
      'Orvexaco builds custom SaaS products end-to-end: multi-tenant architecture, billing, auth, dashboards. Production-ready, not prototypes.',
    h1: 'Custom SaaS Development, Built Production-Ready',
    tagline: 'Multi-tenant architecture, billing, auth, and dashboards — shipped, not prototyped.',
    problem:
      'Most SaaS builds either ship a prototype that breaks at 100 users, or burn months on infrastructure that pays no dividend. Custom SaaS only earns its name when the foundations — auth, billing, multi-tenancy, observability — are real on day one. We see founders pay twice: once for the MVP, again for the rebuild that actually scales.',
    approach:
      'We start with the boring, expensive parts: tenancy boundaries, billing edge cases, audit logs, the migration story you will need in year two. Then we layer the product surface on top so your team can iterate fast without blowing up the platform. Every project ships with CI/CD, observability, and runbooks from day one.',
    deliverables: [
      'Multi-tenant database + auth (organizations, roles, SSO-ready)',
      'Stripe billing: subscription, metered, seat-based, and trial flows',
      'Customer-facing app + internal admin dashboard',
      'CI/CD with preview environments per pull request',
      'Logs, metrics, and error tracking wired in from day one',
      'API + webhook layer for third-party integrations',
      'Handover documentation and on-call runbooks',
    ],
    techStack: [
      'Next.js / React',
      'Node.js / Python',
      'PostgreSQL + Drizzle / Prisma',
      'Stripe',
      'Auth0 / Clerk / custom JWT',
      'AWS / GCP / Vercel',
      'Sentry, Grafana, Datadog',
    ],
    faq: [
      {
        q: 'What does a typical SaaS build timeline look like?',
        a: 'A focused MVP with auth, billing, and one core workflow ships in 6–10 weeks. A full v1 with admin tools, integrations, and analytics typically runs 3–5 months depending on scope.',
      },
      {
        q: 'Do you build multi-tenant from scratch or use a library?',
        a: 'We pick the model that fits the data. For row-level isolation we use PostgreSQL RLS or a tenant-scoped query layer. For stricter separation we use schema-per-tenant or database-per-tenant. We avoid frameworks that lock you into one model before the product is validated.',
      },
      {
        q: 'How do you handle billing complexity (proration, dunning, upgrades)?',
        a: 'Stripe Billing handles proration, invoicing, dunning, and tax calculation natively. We model your plans in Stripe, sync entitlements to your database via webhooks, and add the upgrade/downgrade flows in your app. No bespoke billing engines.',
      },
      {
        q: 'Will I own the code?',
        a: 'Yes. Code lives in your GitHub organization from day one. You own everything and can take any developer over the project at any point.',
      },
      {
        q: 'Do you offer ongoing maintenance after launch?',
        a: 'Yes. Most clients keep us on a retainer for 1–3 months post-launch for hardening, plus on-demand support thereafter. We are also happy to hand off to your in-house team.',
      },
    ],
    relatedSlugs: ['web-development', 'devops'],
    relatedProductSlug: 'acadly-ai',
  },
  {
    slug: 'web-development',
    name: 'Full-Stack Web Development',
    navName: 'Web Development',
    primaryKeyword: 'full-stack web development agency',
    title: 'Full-Stack Web Development Agency',
    description:
      'Full-stack web development by Orvexaco: Next.js, React, Node, Python. Marketing sites, dashboards, internal tools — production code, not landing pages.',
    h1: 'Full-Stack Web Development for Teams Who Ship',
    tagline: 'Marketing sites, dashboards, internal tools — fast, accessible, production-grade.',
    problem:
      'There is no shortage of agencies that build pretty marketing sites or developers that ship dashboards that look like ones from 2014. The real gap is teams that do both well: design language tight enough for a launch page, engineering rigor strong enough for a 50-table admin app.',
    approach:
      'Every web project ships with the same backbone: TypeScript, React, accessible UI primitives, and tested deploy pipelines. We move fast on UI and slow on contracts — once an API or data model is wrong, fixing it later is 5x the cost.',
    deliverables: [
      'Next.js / React app with TypeScript and strict mode',
      'shadcn/ui or custom design system',
      'Server actions, API routes, or dedicated backend (your call)',
      'Database schema + migrations (PostgreSQL or your stack)',
      'Auth, billing, and integrations wired up',
      'WCAG 2.1 AA accessibility baseline',
      'Lighthouse 90+ on mobile out of the gate',
    ],
    techStack: [
      'Next.js 14+ / React 19',
      'TypeScript',
      'Tailwind CSS + shadcn/ui',
      'PostgreSQL + Drizzle / Prisma',
      'Vercel / Cloudflare Pages',
      'Playwright + Vitest',
    ],
    faq: [
      {
        q: 'Do you do design or only development?',
        a: 'Both. We can take a Figma file and ship it pixel-accurate, or design from scratch when there is no design partner. For marketing sites we usually own design end-to-end.',
      },
      {
        q: 'Next.js or plain React?',
        a: 'Next.js for anything that benefits from server rendering, SEO, or full-stack work. Plain Vite + React for embedded apps, dashboards behind auth, or when SSR adds no value. We pick per project, not by default.',
      },
      {
        q: 'How do you handle accessibility?',
        a: 'Every component starts from a WCAG 2.1 AA baseline: keyboard navigation, focus states, semantic HTML, ARIA where it actually helps. We audit with axe-core in CI so regressions are caught before merge.',
      },
      {
        q: 'Will the site be fast?',
        a: 'Yes. We aim for Lighthouse 90+ on mobile at launch. We do not ship 5MB JS bundles to render a marketing page.',
      },
    ],
    relatedSlugs: ['custom-saas-development', 'devops'],
  },
  {
    slug: 'devops',
    name: 'DevOps & Cloud',
    navName: 'DevOps',
    primaryKeyword: 'DevOps consulting services',
    title: 'DevOps Consulting & Cloud Services',
    description:
      'Orvexaco DevOps: CI/CD, Docker/Kubernetes, AWS/GCP, observability, infrastructure as code. Production-grade pipelines for startups and scale-ups.',
    h1: 'DevOps Consulting That Actually Reduces Toil',
    tagline: 'CI/CD, Docker, Kubernetes, AWS/GCP, observability — the boring parts done right.',
    problem:
      'Most teams either ignore DevOps until something is on fire, or over-engineer Kubernetes for an app that runs on a single server. Both are expensive. The right amount of infrastructure is tied to the actual scale and risk of the product, not the trend cycle.',
    approach:
      'We start with what is breaking or about to break: deploys, observability, secrets, on-call. Then we layer in CI/CD, IaC, and scaling primitives in the order that pays off fastest. No Kubernetes for the sake of Kubernetes.',
    deliverables: [
      'CI/CD with build, test, and preview environments per pull request',
      'Containerization (Docker) and orchestration (ECS, GKE, or Kubernetes)',
      'Infrastructure as Code (Terraform or Pulumi)',
      'Centralized logging, metrics, and tracing (OpenTelemetry-ready)',
      'Secrets management (AWS Secrets Manager, Vault, or Doppler)',
      'Incident runbooks and on-call playbooks',
      'Cost visibility and right-sizing recommendations',
    ],
    techStack: [
      'AWS, GCP, Vercel, Cloudflare',
      'Docker, Kubernetes, ECS',
      'Terraform, Pulumi',
      'GitHub Actions, GitLab CI',
      'Grafana, Loki, Prometheus, Datadog',
      'Sentry, OpenTelemetry',
    ],
    faq: [
      {
        q: 'Do we need Kubernetes?',
        a: 'Probably not. Most products under 10 engineers run better on a managed platform like ECS, Cloud Run, or Vercel. Kubernetes is the right answer when you have multiple workloads with real scheduling complexity, not before.',
      },
      {
        q: 'Can you take over an existing AWS account?',
        a: 'Yes. We start with an audit: cost, security, IAM hygiene, what is unmanaged. Then we agree on what to bring under IaC and what to leave alone, in priority order.',
      },
      {
        q: 'How do you handle secrets in CI?',
        a: 'Secrets live in a managed vault (AWS Secrets Manager, GCP Secret Manager, or Doppler). CI fetches at runtime via OIDC where supported. We do not commit .env files, and we do not store long-lived AWS keys in CI environment variables.',
      },
      {
        q: 'Do you offer 24/7 on-call?',
        a: 'No. We design for low-toil systems and write runbooks your team can act on. For follow-the-sun on-call we recommend partners; we focus on making sure pages happen rarely.',
      },
    ],
    relatedSlugs: ['custom-saas-development', 'n8n-automation'],
  },
  {
    slug: 'n8n-automation',
    name: 'n8n Automation',
    navName: 'n8n Automation',
    primaryKeyword: 'n8n workflow automation agency',
    title: 'n8n Automation Agency',
    description:
      'Orvexaco builds production n8n workflows: self-hosted setups, custom nodes, and integrations that replace 10+ hours of manual work per week.',
    h1: 'n8n Workflow Automation, Built for Production',
    tagline: 'Self-hosted n8n, custom nodes, and integrations that replace real work — not toy demos.',
    problem:
      'Off-the-shelf Zaps and Make scenarios get you 70% of the way there. The last 30% — error handling, retries, branching logic, custom data transforms, and integrations with internal systems — is where most workflows quietly fall over. Self-hosted n8n is the right answer; getting it deployed safely and maintained is the hard part.',
    approach:
      'We map your manual processes, identify the highest-leverage automations, and ship them as versioned n8n workflows in a self-hosted environment with backups, monitoring, and proper credential management. When the standard nodes are not enough, we write custom n8n nodes for your APIs.',
    deliverables: [
      'Self-hosted n8n (Docker, Kubernetes, or managed)',
      'Production workflows with retries, branching, and error handling',
      'Custom n8n nodes for your internal APIs',
      'Credential management with encrypted vault',
      'Workflow versioning + git-based deployment',
      'Monitoring + alerting on workflow failures',
      'Runbook for adding and modifying workflows in-house',
    ],
    techStack: [
      'n8n (self-hosted)',
      'Docker / Kubernetes',
      'PostgreSQL (n8n state)',
      'Redis (queue mode)',
      'Custom nodes in TypeScript',
      'GitHub Actions for workflow deploys',
    ],
    faq: [
      {
        q: 'n8n vs Zapier vs Make — which should I use?',
        a: 'Zapier and Make are great for non-technical users with simple workflows. n8n wins when you need self-hosting, complex branching, custom code, or higher volume at predictable cost. We pick based on your team and workload, not preference.',
      },
      {
        q: 'Self-hosted or n8n cloud?',
        a: 'Self-hosted is usually the right call once you cross ~5–10 active workflows or need to integrate with internal systems. n8n cloud is fine for getting started or for teams that do not want to operate infrastructure.',
      },
      {
        q: 'Can n8n integrate with our internal APIs?',
        a: 'Yes. n8n has an HTTP node that handles most APIs out of the box. For complex auth, retries, or domain-specific logic, we write custom nodes that your team can also use.',
      },
      {
        q: 'How do you prevent workflow drift between staging and production?',
        a: 'We treat workflows as code: exported as JSON, version-controlled in git, and deployed via CI. The same workflow that runs in staging is the one that ships to prod, with no manual copy-pasting between environments.',
      },
    ],
    relatedSlugs: ['ai-automation', 'devops'],
  },
  {
    slug: 'ai-automation',
    name: 'AI Automation',
    navName: 'AI Automation',
    primaryKeyword: 'AI automation services for business',
    title: 'AI Automation Services for Business',
    description:
      'Orvexaco builds LLM-powered workflows, RAG systems, and AI agents that automate real business work. Production-grade, not chat-bot demos.',
    h1: 'AI Automation Services That Replace Real Work',
    tagline: 'LLM-powered workflows, RAG systems, and AI agents — production-grade, not demo-ware.',
    problem:
      'The gap between an LLM demo and a production AI workflow is enormous. Demos work on the happy path. Production systems hit edge cases, hallucinations, latency, cost overruns, and prompt injection. Most teams ship the demo and discover the gap in week two.',
    approach:
      'We design AI workflows like any other production system: with evals, fallbacks, observability, and a budget. RAG when retrieval is the win. Tools/function-calling when the LLM needs to act. Caching and routing to keep cost predictable. And humans in the loop for anything that touches money or external communication.',
    deliverables: [
      'LLM workflow design with prompt engineering and evals',
      'RAG pipelines (chunking, embedding, retrieval, reranking)',
      'AI agents with tool use, planning, and guardrails',
      'Document processing: extraction, classification, summarization',
      'Cost monitoring and routing across providers',
      'Prompt versioning + eval harness (Promptfoo or custom)',
      'Human-in-the-loop review for high-stakes outputs',
    ],
    techStack: [
      'Anthropic Claude, OpenAI, open-source LLMs',
      'LangChain, LlamaIndex, custom orchestration',
      'pgvector, Pinecone, Weaviate',
      'Promptfoo, Braintrust, custom evals',
      'Python / TypeScript',
      'Redis for caching',
    ],
    faq: [
      {
        q: 'Should I use Claude, GPT, or open-source?',
        a: 'For most production tasks Claude or GPT win on quality-per-token. Open-source is right when you need data residency, fine-tuning at scale, or predictable per-request cost at high volume. We pick based on the workload and run real evals before committing.',
      },
      {
        q: 'RAG or fine-tuning?',
        a: 'RAG when the answer needs current or proprietary context. Fine-tuning when you need a specific output style or format the base model cannot follow reliably. Most production systems are RAG with prompt engineering — fine-tuning is the last 10%.',
      },
      {
        q: 'How do you handle hallucinations?',
        a: 'We design around them: structured outputs validated with Zod or JSON Schema, retrieval-grounded answers with cited sources, evals that catch regressions, and human review for high-stakes outputs. We do not ship workflows where a hallucination causes silent damage.',
      },
      {
        q: 'What does this cost to run?',
        a: 'Production AI workflows typically run $200–$5000/month in inference cost depending on volume. We model the unit economics before building so you know the cost-per-task and where to optimize.',
      },
    ],
    relatedSlugs: ['ai-agents', 'n8n-automation'],
    relatedProductSlug: 'viralcut',
  },
  {
    slug: 'ai-agents',
    name: 'AI Agents & Custom AI Products',
    navName: 'AI Agents',
    primaryKeyword: 'AI agent development services',
    title: 'AI Agent Development Services',
    description:
      'Orvexaco builds AI agents and custom AI products end-to-end. From idea validation to production launch with evals, guardrails, and real users.',
    h1: 'AI Agents and Custom AI Products, From Idea to Launch',
    tagline: 'Bespoke AI products with real planning, real tool use, and real guardrails.',
    problem:
      'Most "AI agent" projects are a wrapper around a single LLM call labeled as autonomous. Real agents — ones that plan, use tools, recover from failures, and stay within scope — require deliberate engineering. The same is true for custom AI products: the model is 30% of the work; the surface around it is the rest.',
    approach:
      'We build agents and AI products as full systems: a planner, a set of well-defined tools, structured outputs, evals, and clear escalation paths when the agent is uncertain. We start with the narrowest possible task that delivers real value, validate it with evals, and only then expand the agent surface.',
    deliverables: [
      'Agent architecture with planning + tool use + memory',
      'Tool layer with structured inputs/outputs and validation',
      'Eval harness with golden examples and regression tests',
      'Guardrails: scope limits, cost limits, escalation triggers',
      'Observability for agent runs (traces, decisions, tool calls)',
      'Custom AI product UX (chat, command bar, embedded, or ambient)',
      'End-to-end product launch including auth, billing, and analytics',
    ],
    techStack: [
      'Anthropic Claude, OpenAI',
      'LangGraph, custom agent loops',
      'Function/tool calling, MCP',
      'Promptfoo, Braintrust',
      'Next.js / React for product UI',
      'PostgreSQL + pgvector',
    ],
    faq: [
      {
        q: 'When does an agent make sense vs a simple prompt?',
        a: 'When the task requires multiple steps, real-time decisions, or tool use to gather information. If a single prompt with retrieval can do it, do not build an agent. Agents add cost, latency, and failure modes.',
      },
      {
        q: 'How do you stop the agent from going off the rails?',
        a: 'Scope limits in the system prompt, hard caps on iterations and cost per run, structured outputs that gate every action, and escalation to a human when confidence is low. The guardrails are not optional — they are the product.',
      },
      {
        q: 'What is MCP and should we use it?',
        a: 'Model Context Protocol is an open standard for giving LLMs access to tools and data sources. It is worth using when you want the same toolset to work across Claude, ChatGPT, and other clients. For internal-only agents, a custom tool layer is often simpler.',
      },
      {
        q: 'Can you launch an AI product end-to-end?',
        a: 'Yes. We have shipped Acadly AI, ATS Resume Helper, and Viral Cut as full products with auth, billing, analytics, and ongoing iteration. We can do the same for your concept.',
      },
    ],
    relatedSlugs: ['ai-automation', 'custom-saas-development'],
    relatedProductSlug: 'ats-resume-helper',
  },
  {
    slug: 'mobile-app-development',
    name: 'Mobile App Development',
    navName: 'Mobile App Dev',
    primaryKeyword: 'mobile app development services',
    title: 'Mobile App Development Services',
    description:
      'Orvexaco mobile app development: iOS, Android, and React Native. Production-grade native and cross-platform apps with backends, analytics, and CI/CD.',
    h1: 'Mobile Apps Built for Production, Not the Demo',
    tagline: 'iOS, Android, React Native — native quality without picking sides.',
    problem:
      'Most mobile builds end at "the demo runs on my phone." Production apps need a backend, push, crash reporting, analytics, store submission, and a deploy pipeline that does not break on the next OS update. Most agencies stop at the UI; the rest is left to the client to figure out.',
    approach:
      'We pick the stack based on the app, not the trend cycle. Native (Swift/Kotlin) when performance or platform features demand it, React Native or Flutter when the team is small and the surface is large. Either way the build ships with the same backbone: backend API, push, crash reporting, analytics, CI/CD, and a documented store-submission process.',
    deliverables: [
      'Native iOS (Swift) or Android (Kotlin) — when needed',
      'React Native or Flutter cross-platform builds',
      'Backend API + push notifications + auth',
      'App Store + Play Store submission and metadata',
      'Crash reporting (Sentry) + analytics (Amplitude / GA4)',
      'CI/CD with TestFlight + Firebase App Distribution',
      'Over-the-air updates where the platform allows',
    ],
    techStack: [
      'React Native + Expo',
      'Swift / Kotlin (native)',
      'Flutter',
      'Firebase / Supabase',
      'Sentry, Amplitude',
      'Fastlane, EAS Build',
    ],
    faq: [
      {
        q: 'Native or cross-platform?',
        a: 'Cross-platform (React Native / Flutter) for most apps where the surface is shared between iOS and Android and performance is fine. Native when you need deep platform features, gaming-grade performance, or one platform truly dominates your user base. We do not religiously pick one; we look at the app.',
      },
      {
        q: 'Do you handle App Store + Play Store submission?',
        a: 'Yes. We submit the first version, set up the store listings, screenshots, and review responses. We also document the renewal process so your team can take it over.',
      },
      {
        q: 'How do you handle the backend?',
        a: 'Either we build it (typical with our custom SaaS service) or integrate with the one you already have. We are stack-agnostic on backends — Node, Python, Go, or your existing API.',
      },
      {
        q: 'How do you keep up with iOS / Android updates?',
        a: 'We pin SDK versions, run regular dependency upgrades, and test against the latest betas. Apps left in the wild without maintenance break — usually within 12 months. We recommend a small ongoing retainer for OS-update work.',
      },
    ],
    relatedSlugs: ['custom-saas-development', 'api-development'],
  },
  {
    slug: 'ui-ux-design',
    name: 'Product UI/UX Design',
    navName: 'UI/UX Design',
    primaryKeyword: 'product UI UX design services',
    title: 'Product UI/UX Design Services',
    description:
      'Orvexaco UI/UX design: product design, design systems, user flows, prototyping. Figma files engineers can actually build, not pretty mockups that miss the spec.',
    h1: 'Design That Engineers Can Actually Build',
    tagline: 'Design systems, product UX, prototyping — handed off ready to ship.',
    problem:
      'Most product design dies in the handoff. The Figma is gorgeous, the engineer asks "what happens on this edge case," and there is no answer. Then come the rebuild rounds. Real product design covers states, edge cases, accessibility, and ships with a system the team can extend without hiring a designer for every screen.',
    approach:
      'We design in Figma with components and tokens that map 1:1 to your code (shadcn/ui, Tailwind, or whatever your engineers actually use). Every flow ships with empty / loading / error / success states. Every component carries usage notes. Engineers should never have to guess.',
    deliverables: [
      'User research synthesis (lightweight, not a 60-page deck)',
      'Information architecture + user flows',
      'Mid-fi wireframes + hi-fi UI in Figma',
      'Design system with tokens, components, and usage notes',
      'Accessibility annotations (WCAG 2.1 AA)',
      'Interactive prototype for stakeholder review',
      'Engineering-ready handoff with specs',
    ],
    techStack: [
      'Figma + Figma Tokens',
      'shadcn/ui mapping',
      'Tailwind CSS',
      'Storybook for component docs',
      'Maze / Useberry for usability testing',
    ],
    faq: [
      {
        q: 'Can you design without engineering?',
        a: 'Yes. We do design-only engagements when you have an in-house team that just needs design capacity. The design system we deliver is built to map to common implementation stacks, so handoff to your engineers is straightforward.',
      },
      {
        q: 'Do you do user research?',
        a: 'Lightweight: stakeholder interviews, synthesis of existing data, and usability testing on prototypes. We do not run multi-month research engagements; for those we recommend specialist research firms.',
      },
      {
        q: 'How do you handle design systems?',
        a: 'We build with tokens (color, spacing, type) and components, exported in Figma variables. The system is sized to your product — we do not ship a 200-component library for a 12-screen app.',
      },
    ],
    relatedSlugs: ['web-development', 'custom-saas-development'],
  },
  {
    slug: 'api-development',
    name: 'API Development & Integration',
    navName: 'API Development',
    primaryKeyword: 'API development services',
    title: 'API Development and Integration Services',
    description:
      'Orvexaco API development: REST and GraphQL APIs, third-party integrations, webhook systems, API gateways. Versioned, documented, monitored from day one.',
    h1: 'APIs Built for the Long Run',
    tagline: 'REST, GraphQL, webhooks, integrations — versioned, documented, monitored.',
    problem:
      'APIs are easy to build and hard to maintain. The first version ships fast; year two is when authentication is a mess, versioning is impossible, the docs are out of date, and every integration is one bad deploy away from breaking. Real API design pays the maintenance tax up front.',
    approach:
      'We design APIs contract-first: OpenAPI for REST or schema-first for GraphQL. Versioning, auth, rate limiting, and observability are wired in before the first endpoint goes live. Documentation is generated from the contract, so it stays correct.',
    deliverables: [
      'REST (OpenAPI) or GraphQL schema-first design',
      'Authentication (OAuth2, JWT, API keys) + RBAC',
      'Rate limiting, retries, idempotency keys',
      'Webhook delivery system with retries + signatures',
      'Auto-generated docs (Stoplight, Redoc, GraphiQL)',
      'Third-party integrations (Stripe, HubSpot, Salesforce, etc.)',
      'Monitoring + alerts (latency, error rate, contract drift)',
    ],
    techStack: [
      'Node.js / Python / Go',
      'OpenAPI 3.1 + Orval',
      'GraphQL (Apollo, Hasura)',
      'PostgreSQL + Redis',
      'Kong / API Gateway',
      'Datadog, OpenTelemetry',
    ],
    faq: [
      {
        q: 'REST or GraphQL?',
        a: 'REST for public-facing or simple CRUD APIs. GraphQL when clients need flexibility (mobile apps with varied views, admin tools that aggregate from many sources). We pick per project. Most product APIs are fine as REST + OpenAPI.',
      },
      {
        q: 'How do you handle versioning?',
        a: 'URL versioning (/v1, /v2) for major breaking changes; non-breaking additions ship to the current version. Deprecation policy is documented up front. We do not silently change response shapes.',
      },
      {
        q: 'Can you integrate with our existing systems?',
        a: 'Yes. Common integrations: Stripe, Salesforce, HubSpot, NetSuite, Slack, vendor APIs with bad docs. We document every integration so the next engineer does not have to reverse-engineer it.',
      },
      {
        q: 'Do you build internal APIs only, or public APIs too?',
        a: 'Both. Public APIs need extra: developer portal, sandboxes, SLA monitoring, and a versioning policy you can stand by for years.',
      },
    ],
    relatedSlugs: ['custom-saas-development', 'devops'],
  },
  {
    slug: 'data-engineering',
    name: 'Data Engineering & Analytics',
    navName: 'Data Engineering',
    primaryKeyword: 'data engineering services',
    title: 'Data Engineering and Analytics Services',
    description:
      'Orvexaco data engineering: data warehouses, ETL pipelines, BI dashboards, real-time analytics. From scattered SaaS data to a single source of truth.',
    h1: 'Turn Scattered Data Into Decisions',
    tagline: 'Warehouses, pipelines, dashboards — single source of truth, not 5 tabs.',
    problem:
      'Every team has 12 SaaS tools and zero unified view. Revenue is in Stripe, leads are in HubSpot, product usage is in PostHog, support tickets are in Intercom. Decisions get made on whichever number someone happened to look at this morning. A real data layer makes this go away.',
    approach:
      'We build the boring, durable layer first: a managed warehouse (Snowflake, BigQuery, or Postgres if scale is small), Fivetran/Airbyte for ingestion, dbt for transformations, and a BI tool the team will actually use. Real-time and ML come later, on top of that foundation.',
    deliverables: [
      'Data warehouse setup (Snowflake / BigQuery / PostgreSQL)',
      'ETL/ELT pipelines (Fivetran, Airbyte, custom)',
      'dbt models with tests, docs, and lineage',
      'BI dashboards (Metabase, Looker, Hex)',
      'Reverse ETL (warehouse → CRM / ops tools)',
      'Event tracking schemas (PostHog, Segment)',
      'Real-time analytics where it actually matters',
    ],
    techStack: [
      'Snowflake, BigQuery, PostgreSQL',
      'Fivetran, Airbyte',
      'dbt',
      'Metabase, Looker, Hex',
      'PostHog, Segment',
      'Apache Kafka (when streaming is real)',
    ],
    faq: [
      {
        q: 'Snowflake, BigQuery, or PostgreSQL?',
        a: 'PostgreSQL is fine up to ~100GB and a small team. BigQuery is the cheapest serverless option at scale. Snowflake is the most flexible. We pick based on volume, query patterns, and what your team can operate.',
      },
      {
        q: 'Do we need a data team?',
        a: 'For setup, no — we ship a working pipeline + dashboards. For ongoing analytics, you need at least one person who owns the data layer (analyst, analytics engineer, or technical PM). We can train your existing team during the build.',
      },
      {
        q: 'What about ML and real-time?',
        a: 'Both are real but expensive to start. Most teams get 80% of the value from a clean batch warehouse and good dashboards. We add streaming or ML when there is a specific decision that depends on it, not as a default.',
      },
    ],
    relatedSlugs: ['ai-automation', 'devops'],
  },
  {
    slug: 'ecommerce-development',
    name: 'E-commerce Development',
    navName: 'E-commerce',
    primaryKeyword: 'custom e-commerce development services',
    title: 'Custom E-commerce Development Services',
    description:
      'Orvexaco e-commerce builds: Shopify Plus, headless commerce, custom checkouts, payment integrations. E-commerce that survives Black Friday traffic.',
    h1: 'E-commerce That Ships and Scales',
    tagline: 'Shopify Plus, headless commerce, custom checkouts — built for traffic.',
    problem:
      'Off-the-shelf Shopify is great until you hit the limits. Custom checkout, B2B pricing rules, complex catalog logic, multi-storefront — the stuff that makes you money is exactly the stuff the platform fights you on. The wrong build at the wrong time costs months and revenue.',
    approach:
      'We start with the simplest platform that fits the business: Shopify for most DTC, Shopify Plus when you need scripts/B2B, headless (Shopify or commercetools) when the storefront has unique requirements. We resist over-engineering — but when you have outgrown Shopify, we tell you, and we build the headless stack properly.',
    deliverables: [
      'Shopify or Shopify Plus theme + functionality',
      'Headless storefronts (Next.js + Shopify Storefront API or commercetools)',
      'Custom checkout extensions (Shopify Functions, Plus scripts)',
      'B2B / wholesale flows, custom pricing rules',
      'Payment integrations (Stripe, Adyen, Klarna, regional gateways)',
      'Performance + Core Web Vitals tuning for store pages',
      'PIM / OMS integrations',
    ],
    techStack: [
      'Shopify + Shopify Plus',
      'Next.js (headless storefront)',
      'commercetools',
      'Stripe, Adyen, Klarna',
      'Algolia (search)',
      'Sanity / Contentful (content)',
    ],
    faq: [
      {
        q: 'Shopify or custom?',
        a: 'Shopify for 80% of DTC stores under $50M GMV. Shopify Plus when you need scripts, B2B, or higher API limits. Custom or headless only when the constraints are real — not because someone read a thought-leader post about platform limits.',
      },
      {
        q: 'Headless — when is it actually worth it?',
        a: 'When the storefront has unique UX requirements (configurators, content-heavy editorial), the team has Next.js capability, and the additional complexity pays for itself. Otherwise stick with the native theme.',
      },
      {
        q: 'Can you migrate us off WooCommerce / Magento?',
        a: 'Yes. We have done WooCommerce → Shopify and Magento → Shopify Plus migrations. Catalog, orders, customers, redirects, SEO carryover all included.',
      },
    ],
    relatedSlugs: ['custom-saas-development', 'web-development'],
  },
  {
    slug: 'seo-audit',
    name: 'SEO Audit',
    navName: 'SEO Audit',
    primaryKeyword: 'technical SEO audit services',
    title: 'Technical SEO Audit Services',
    description:
      'Orvexaco SEO audits: indexing, schema, Core Web Vitals, internal linking, content gaps. Written report with prioritized P0/P1/P2 fixes you can act on.',
    h1: 'SEO Audits That Actually Move Rankings',
    tagline: 'Deep technical audit, prioritized fix list, written report. Implementation optional.',
    problem:
      'Most SEO audits are PDFs full of generic recommendations: "improve page speed", "add meta descriptions", "build backlinks." That is not an audit, that is a checklist anyone could write. A real audit names the specific URLs that are broken, the specific schema that is invalid, the specific reason a page is not indexed, and the order to fix them in.',
    approach:
      'We crawl the site, pull live data from Google Search Console, run real Lighthouse and Rich Results tests, and write a report that names files and URLs. The fix list is prioritized P0 (blocks indexing) → P1 (kills rankings) → P2 (polish), with effort estimates. We deliver a written report in 5-10 days.',
    deliverables: [
      'Crawl + indexing analysis (sitemap vs reality, redirect chains, canonical conflicts)',
      'Core Web Vitals review with named offending resources',
      'Schema markup validation across every template',
      'On-page audit (titles, meta, H1 hierarchy, alt text, internal linking)',
      'Search Console deep-dive (impressions, clicks, queries, errors)',
      'Backlink profile snapshot + toxic-link flags',
      'Prioritized P0/P1/P2 fix list with effort estimates',
      'Written report (15-30 pages) — not a slide deck',
    ],
    techStack: [
      'Screaming Frog / Sitebulb',
      'Google Search Console API',
      'Ahrefs / SEMrush',
      'Lighthouse + WebPageTest',
      'Google Rich Results Test',
      'Custom crawl scripts where needed',
    ],
    faq: [
      {
        q: 'How long does an audit take?',
        a: 'Typically 5–10 working days from kickoff to delivered report, depending on site size. Sites under 500 URLs we can turn around in a week; larger sites or sites with weird tech stacks take longer.',
      },
      {
        q: 'Will you implement the fixes?',
        a: 'Optional. Many clients take the audit and have their in-house team implement. Others bring us back to do the implementation. We sell both — and we do not pad the audit with dependencies on us.',
      },
      {
        q: 'Do you cover content / keyword strategy too?',
        a: 'The audit covers content gaps and keyword targeting at the page level. For ongoing keyword research and content production, that is our SEO Optimization service.',
      },
      {
        q: 'What if the audit finds nothing critical?',
        a: 'Rare, but it happens — and we will tell you. The report still covers what is healthy and what could be incrementally better. We are not in the business of inventing P0s to justify a fee.',
      },
    ],
    relatedSlugs: ['seo-optimization', 'web-development'],
  },
  {
    slug: 'seo-optimization',
    name: 'SEO Optimization',
    navName: 'SEO Optimization',
    primaryKeyword: 'SEO optimization services',
    title: 'SEO Optimization Services',
    description:
      'Orvexaco SEO optimization: technical fixes, schema, content, internal linking, Core Web Vitals tuning. Implementation, not just audits. Built around your stack.',
    h1: 'SEO Optimization for Teams That Ship',
    tagline: 'Implementation, not slides. Schema, CWV, content, internal linking — wired in.',
    problem:
      'Most SEO agencies sell reports and dashboards. The actual work — writing the schema component, fixing the redirect chain, tuning the LCP element, restructuring internal linking — never gets done. Or it gets done by your engineers who already have a backlog. SEO that ships has to be done by people who can also code.',
    approach:
      'We engage as engineers, not consultants. We take a tight scope, ship the changes in your codebase (not as recommendations in a doc), and measure the result in Search Console over 4-8 weeks. We work in 1-3 month sprints with a defined deliverable per sprint.',
    deliverables: [
      'Technical SEO fixes implemented in your codebase',
      'Schema markup components (Organization, Product, Service, FAQ, Article, Breadcrumb)',
      'Sitemap generation + robots.txt tuning',
      'Core Web Vitals optimization (LCP, INP, CLS)',
      'Internal linking architecture (hub-and-spoke, related-content, breadcrumbs)',
      'Content briefs + on-page optimization for target queries',
      'Search Console monitoring + monthly reports',
      'Migration SEO when you redesign or replatform',
    ],
    techStack: [
      'Whatever your stack uses',
      'Common: Next.js, React, Vite, WordPress, Shopify',
      'Schema: JSON-LD via shared helpers',
      'Search Console + GA4 + Looker Studio',
      'Lighthouse CI for regression checks',
    ],
    faq: [
      {
        q: 'Is this SEO or engineering?',
        a: 'Both. Modern SEO is mostly engineering: schema, performance, crawlability, internal linking architecture. We treat it as production work that ships in your codebase, with PRs and reviews like any other change.',
      },
      {
        q: 'How long until I see ranking changes?',
        a: '4–8 weeks for technical fixes (indexing improvements, CWV gains). 3–6 months for content and authority changes. We set expectations up front and report on lead indicators (impressions, average position) before money rankings move.',
      },
      {
        q: 'Do you do link building?',
        a: 'No. We focus on the technical, on-page, and content layers we can directly control. For outreach and digital PR we recommend specialist partners.',
      },
      {
        q: 'Can you handle a site migration?',
        a: 'Yes. Migration SEO is one of the highest-stakes engagements: get it wrong and you lose 50% of traffic for 6 months. We plan the redirect map, schema continuity, and validation steps before the migration ships.',
      },
    ],
    relatedSlugs: ['seo-audit', 'web-development'],
  },
  {
    slug: 'it-services',
    name: 'IT & Software Consulting',
    navName: 'IT Consulting',
    primaryKeyword: 'IT consulting and software services',
    title: 'IT Consulting and Software Services',
    description:
      'Orvexaco IT consulting: software audits, integrations, modernization, and team augmentation. Senior engineers without the agency overhead.',
    h1: 'IT Consulting and Software Services',
    tagline: 'Audits, integrations, modernization, and team augmentation — senior engineers, no overhead.',
    problem:
      'Not every project needs a full SaaS build. Sometimes you need an honest audit of a legacy system, an integration with a vendor that has bad docs, a modernization plan, or a senior engineer for two months. Most agencies will not take this work — too small, too messy. We will.',
    approach:
      'We scope tightly, work fast, and stay practical. Audits produce a written report with prioritized findings, not slideware. Integrations are documented and tested. Modernization plans are sequenced so each step delivers value, not just the final landing.',
    deliverables: [
      'Software, security, or architecture audit with prioritized findings',
      'Third-party integrations (CRMs, ERPs, payment systems, vendor APIs)',
      'Legacy modernization plan + incremental execution',
      'Senior engineer team augmentation (1–3 month engagements)',
      'Database migrations and data backfills',
      'Performance investigations and fixes',
      'Technical due diligence for acquisitions',
    ],
    techStack: [
      'Stack-agnostic — we work in your stack',
      'Common: Node, Python, PostgreSQL, AWS/GCP',
      'Salesforce, HubSpot, NetSuite integrations',
      'Snowflake, BigQuery, Redshift',
    ],
    faq: [
      {
        q: 'What is a typical audit deliverable?',
        a: 'A written report (10–30 pages) with: an executive summary, prioritized P0/P1/P2 findings, remediation steps with effort estimates, and a 30/60/90-day roadmap. We do not do PowerPoint-only audits.',
      },
      {
        q: 'Will you work in my existing stack?',
        a: 'Yes. We are stack-agnostic for consulting work. We have shipped in Ruby on Rails, .NET, PHP, Java, Go, and most JavaScript runtimes. We tell you up front if your stack is outside our depth.',
      },
      {
        q: 'How long is a typical engagement?',
        a: 'Audits run 1–3 weeks. Integrations 2–6 weeks. Team augmentation 1–3 months with extension options. We do not lock clients into year-long retainers for ad-hoc work.',
      },
      {
        q: 'Do you do due diligence for acquisitions?',
        a: 'Yes. Technical DD covering code quality, architecture, security, scalability, and team capability. Output is a written report the deal team can use.',
      },
    ],
    relatedSlugs: ['custom-saas-development', 'devops'],
  },
];

export const servicesBySlug: Record<string, Service> = Object.fromEntries(
  services.map((s) => [s.slug, s]),
);
